import React, { useRef, useState, useEffect } from 'react';
import {
  CanvasSection,
  CanvasArea,
  DropZone,
  UploadIconWrapper,
  DropZonePrimary,
  DropZoneSecondary,
  DropZoneUploadButton,
  DropZoneFormats,
  HiddenFileInput,
  ErrorText,
  ImageFrame,
  UploadedImage,
  SelectionBox,
  HandleTL,
  HandleTC,
  HandleTR,
  HandleML,
  HandleMR,
  HandleBL,
  HandleBC,
  HandleBR,
  StatusBanner,
  StatusSpinner,
  HistoryControlBar,
  HistoryButton,
  HistoryDivider,
} from '../imageCanvasStyles';
import { EditOutlined } from 'components/icons';
import { CropOverlay } from './CropOverlay';
import { RotateOverlay } from './RotateOverlay';
import { AdjustOverlay, DEFAULT_ADJUSTMENTS, buildFilterString } from './AdjustOverlay';
import { ResizeOverlay } from './ResizeOverlay';

const ACCEPTED_TYPES = [ 'image/png', 'image/jpeg', 'image/jpg', 'image/webp' ];

const ImageUploadCanvas = ({
  theme,
  selectedTheme,
  previewUrl,
  zoom,
  isDragOver,
  error,
  status,
  onFiles,
  onDropFiles,
  onDragOver,
  onDragLeave,
  selectedTool,
  canUndo,
  canRedo,
  canReset,
  onUndo,
  onRedo,
  onResetEdits,
  onCropApply,
  onCropCancel,
  onRotateApply,
  onRotateCancel,
  onAdjustApply,
  onResizeApply,
  onResizeCancel,
  onEditImage,
}) => {
  const inputRef = useRef(null);
  const imgRef = useRef(null);
  const shared = { theme, $selectedTheme: selectedTheme };
  const isCropMode = selectedTool === 'crop';
  const isRotateMode = selectedTool === 'rotate';
  const isAdjustMode = selectedTool === 'adjust';
  const isResizeMode = selectedTool === 'resize';
  const hasActiveOverlay = isCropMode || isRotateMode || isAdjustMode || isResizeMode;

  const [ rotateAngle, setRotateAngle ] = useState(0);
  const [ adjustments, setAdjustments ] = useState(DEFAULT_ADJUSTMENTS);

  useEffect(() => {
    if (!isRotateMode) setRotateAngle(0);
  }, [ isRotateMode, previewUrl ]);

  useEffect(() => {
    if (!isAdjustMode) setAdjustments(DEFAULT_ADJUSTMENTS);
  }, [ isAdjustMode, previewUrl ]);

  const handleDrop = e => {
    e.preventDefault();
    onDragLeave();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) onDropFiles(files);
  };

  const handleInputChange = e => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) onFiles(files);
    e.target.value = '';
  };

  const handleClick = () => inputRef.current?.click();

  const handleUploadButtonClick = e => {
    e.stopPropagation();
    handleClick();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleAdjustReset = () => setAdjustments(DEFAULT_ADJUSTMENTS);

  return (
    <CanvasSection {...shared}>
      <CanvasArea>
        {previewUrl ? (
          <ImageFrame {...shared} $zoom={zoom}>
            <UploadedImage
              ref={imgRef}
              src={previewUrl}
              alt='Uploaded image'
              $rotate={isRotateMode ? rotateAngle : 0}
              $filter={isAdjustMode ? buildFilterString(adjustments) : 'none'}
            />
            {!hasActiveOverlay && (
              <SelectionBox>
                <HandleTL /><HandleTC /><HandleTR />
                <HandleML /><HandleMR />
                <HandleBL /><HandleBC /><HandleBR />
              </SelectionBox>
            )}
            {isCropMode && (
              <CropOverlay imgRef={imgRef} onApply={onCropApply} onCancel={onCropCancel} />
            )}
          </ImageFrame>
        ) : (
          <DropZone
            {...shared}
            $isDragOver={isDragOver}
            onClick={handleClick}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={handleDrop}
            role='button'
            tabIndex={0}
            onKeyDown={handleKeyDown}
            aria-label='Upload image area'
          >
            <UploadIconWrapper {...shared}>&#128444;</UploadIconWrapper>
            <DropZonePrimary {...shared}>Drag and drop your image here</DropZonePrimary>
            <DropZoneSecondary {...shared}>or click to upload</DropZoneSecondary>
            <DropZoneUploadButton {...shared} type='button' onClick={handleUploadButtonClick}>
              <span aria-hidden='true'>&#8682;</span> Upload Image
            </DropZoneUploadButton>
            <DropZoneFormats {...shared}>Supported formats: PNG, JPG, JPEG, WEBP</DropZoneFormats>
            {error && <ErrorText {...shared}>{error}</ErrorText>}
          </DropZone>
        )}

        <HiddenFileInput
          ref={inputRef}
          type='file'
          multiple
          accept={ACCEPTED_TYPES.join(',')}
          onChange={handleInputChange}
        />
      </CanvasArea>

      {previewUrl && !hasActiveOverlay && (
        <HistoryControlBar {...shared} role='toolbar' aria-label='Edit history'>
          {onEditImage && (
            <>
              <HistoryButton
                {...shared}
                $primary
                type='button'
                onClick={onEditImage}
                title='Edit image'
                aria-label='Edit image'
              >
                <EditOutlined
                  width={16}
                  height={16}
                  color={theme.colors[selectedTheme].accent}
                  hoverColor={theme.colors[selectedTheme].accent}
                />
                Edit Image
              </HistoryButton>
              <HistoryDivider {...shared} aria-hidden='true' />
            </>
          )}
          <HistoryButton
            {...shared}
            type='button'
            onClick={onUndo}
            disabled={!canUndo}
            title='Undo (Ctrl+Z)'
            aria-label='Undo'
          >
            <span aria-hidden='true'>&#8630;</span>
          </HistoryButton>
          <HistoryButton
            {...shared}
            type='button'
            onClick={onRedo}
            disabled={!canRedo}
            title='Redo (Ctrl+Y)'
            aria-label='Redo'
          >
            <span aria-hidden='true'>&#8631;</span>
          </HistoryButton>
          <HistoryDivider {...shared} aria-hidden='true' />
          <HistoryButton
            {...shared}
            $wide
            type='button'
            onClick={onResetEdits}
            disabled={!canReset}
            title={canReset ? 'Revert to original image' : 'No edits to reset'}
            aria-label='Reset to original'
          >
            <span aria-hidden='true'>&#10227;</span> Reset
          </HistoryButton>
        </HistoryControlBar>
      )}

      {status && (
        <StatusBanner {...shared} $variant={status.variant || 'info'}>
          {status.variant === 'info' && <StatusSpinner />}
          <span>{status.message}</span>
        </StatusBanner>
      )}

      {previewUrl && isRotateMode && (
        <RotateOverlay
          theme={theme}
          selectedTheme={selectedTheme}
          imgRef={imgRef}
          angle={rotateAngle}
          onAngleChange={setRotateAngle}
          onApply={onRotateApply}
          onCancel={onRotateCancel}
        />
      )}

      {previewUrl && isAdjustMode && (
        <AdjustOverlay
          theme={theme}
          selectedTheme={selectedTheme}
          imgRef={imgRef}
          adjustments={adjustments}
          onAdjustmentsChange={setAdjustments}
          onApply={onAdjustApply}
          onReset={handleAdjustReset}
        />
      )}

      {previewUrl && isResizeMode && (
        <ResizeOverlay
          theme={theme}
          selectedTheme={selectedTheme}
          imgRef={imgRef}
          onApply={onResizeApply}
          onCancel={onResizeCancel}
        />
      )}

    </CanvasSection>
  );
};

export { ImageUploadCanvas };
